// Dashboard.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { usePublishShellChrome, ShellSectionActions } from 'context/ShellChromeContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useBase } from 'context/BaseContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Services
import { deleteDashboardByKey, updateDashboard } from 'services/dashboards/DashboardWriteService';
import { update as updateUser } from 'services/users/UserService';

// Hooks
import useDashboardLayoutSourceData from 'dashboard/hooks/useDashboardLayoutSourceData';
import usePeekPanelController from 'node/hooks/usePeekPanelController';
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Section from 'components/section/Section';
import IconButton from 'components/iconbutton/IconButton';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import DashboardHeader from 'dashboard/components/DashboardHeader';
import DashboardLayoutEditor from 'dashboard/DashboardLayoutEditor';
import DashboardRenderer from 'dashboard/DashboardRenderer';
import DashboardModal from 'main/sidebar/dashboards/DashboardModal';
import PeekPanel from 'node/peek/PeekPanel';

// CSS
import './Dashboard.css';

/**
 * Dashboard
 *
 * Base dashboard with browse (DashboardRenderer) and edit (DashboardLayoutEditor) modes.
 *
 * @param {object} dashboard - Dashboard document.
 * @param {array|null} allowedTypeKeys - Optional allowed node type keys for view picking.
 * @param {boolean} showHeader - Whether to show the dashboard title header.
 * @param {boolean} [publishShellChrome] - Publish identity to GlobalToolbar on desktop (defaults to showHeader).
 * @param {function|null} onOpenView - Optional callback when a view title is opened.
 *
 * @returns {ReactNode}
 */
const Dashboard = React.memo(({
    dashboard,
    allowedTypeKeys = null,
    showHeader = false,
    publishShellChrome,
    onOpenView = null,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();
    const { getType } = useNodeTypes();
    const { user } = useUser();
    const { activeBase } = useBase();
    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [dashboardSettingsOpen, setDashboardSettingsOpen] = useState(false);
    const [, setIsPinUpdating] = useState(false);
    const [isLayoutEditMode, setIsLayoutEditMode] = useState(false);
    // ----- ROUTING ----- //

    const navigate = useNavigate();
    // ----- HOOKS ----- //

    const {
        isPeekOpen,
        closePeek,
        peekLocalNode,
        isPeekLoading,
        peekError,
        handlePeekTitleSaved,
        handlePeekTagsSaved,
        handlePeekLocationSaved,
        handlePeekSubtitleSaved,
        handlePeekCoverSaved,
        handlePeekNodeChange,
    } = usePeekPanelController({
        user,
        setCurrentNode: null,
    });

    const activeBaseKey = typeof activeBase?.key === 'string'
        ? activeBase.key.trim()
        : '';

    const {
        childNodesByParent,
        views,
        baseNodes,
    } = useDashboardLayoutSourceData({
        dashboard,
        baseKey: activeBaseKey,
    });

    // ----- MEMOS ----- //

    const dashboardIconKey = useMemo(() => {
        const raw = String(dashboard?.iconKey || '').trim();
        return raw || 'dashboard';
    }, [dashboard?.iconKey]);

    const dashboardTitle = useMemo(() => {
        const raw = String(dashboard?.title || '').trim();
        return raw || 'Untitled Dashboard';
    }, [dashboard?.title]);

    const shouldPublishShellChrome = publishShellChrome ?? showHeader;
    const hideHeaderIdentity = showHeader && shouldPublishShellChrome && !isMobile;
    const actionsInShell = hideHeaderIdentity;

    usePublishShellChrome({
        title: dashboardTitle,
        iconKey: dashboardIconKey,
        enabled: hideHeaderIdentity,
    });

    const pinnedDashboardByBase = useMemo(() => {
        const candidate = user?.data_profile?.pinnedDashboardByBase;
        if (!candidate || typeof candidate !== 'object') return {};
        return candidate;
    }, [user?.data_profile?.pinnedDashboardByBase]);

    const pinnedDashboardKey = useMemo(() => {
        if (!activeBaseKey) return '';
        const value = pinnedDashboardByBase[activeBaseKey];
        return typeof value === 'string' ? value.trim() : '';
    }, [activeBaseKey, pinnedDashboardByBase]);

    const isPinnedToHome = Boolean(
        dashboard?.key
        && pinnedDashboardKey
        && pinnedDashboardKey === dashboard.key
    );

    // ----- HANDLERS ----- //

    const handleDashboardTitleSaved = useCallback(async (nextTitleValue) => {
        if (!dashboard?.key) return;

        const normalized = String(nextTitleValue || '').trim();
        const nextTitle = normalized || 'Untitled Dashboard';
        const currentTitle = String(dashboard?.title || '').trim() || 'Untitled Dashboard';

        if (nextTitle === currentTitle) return;

        try {
            await updateDashboard(dashboard.key, {
                title: nextTitle,
                titleLower: nextTitle.toLowerCase(),
            });
        } catch (error) {
            console.error('[Dashboard] update title error:', error);
        }
    }, [dashboard?.key, dashboard?.title]);

    const handleSelectDashboardIcon = useCallback(async (selectedKey, { closePicker } = {}) => {
        if (!dashboard?.key) return;

        const nextIconKey = typeof selectedKey === 'string' ? selectedKey.trim() : '';

        try {
            await updateDashboard(dashboard.key, {
                iconKey: nextIconKey,
            });
            closePicker?.();
        } catch (error) {
            console.error('[Dashboard] update icon error:', error);
        }
    }, [dashboard?.key]);

    // ----- MEMOS ----- //

    const persistPinnedDashboardPreference = useCallback(async (nextPinnedDashboardKey) => {
        if (!user?.key || !activeBaseKey) return;

        const normalizedKey = typeof nextPinnedDashboardKey === 'string'
            ? nextPinnedDashboardKey.trim()
            : '';

        const nextMap = {
            ...pinnedDashboardByBase,
        };

        if (normalizedKey) {
            nextMap[activeBaseKey] = normalizedKey;
        } else {
            delete nextMap[activeBaseKey];
        }

        await updateUser(user.key, {
            'data_profile.pinnedDashboardByBase': Object.keys(nextMap).length ? nextMap : null,
        });
    }, [activeBaseKey, pinnedDashboardByBase, user?.key]);

    // ----- HANDLERS ----- //

    const handleTogglePinToHome = useCallback(async () => {
        const dashboardKey = String(dashboard?.key || '').trim();
        if (!dashboardKey || !activeBaseKey || !user?.key) return;

        try {
            setIsPinUpdating(true);
            await persistPinnedDashboardPreference(isPinnedToHome ? '' : dashboardKey);
        } catch (error) {
            console.error('[Dashboard] update pinned dashboard error:', error);
        } finally {
            setIsPinUpdating(false);
        }
    }, [activeBaseKey, dashboard?.key, isPinnedToHome, persistPinnedDashboardPreference, user?.key]);

    const handleDeleteDashboard = useCallback(async () => {
        const dashboardKey = String(dashboard?.key || '').trim();
        if (!dashboardKey) return;

        const dashboardTitle = String(dashboard?.title || '').trim() || 'Untitled Dashboard';
        const confirmed = window.confirm(`Delete dashboard "${dashboardTitle}" and all imported config?`);
        if (!confirmed) return;

        try {
            if (isPinnedToHome) {
                await persistPinnedDashboardPreference('');
            }
            await deleteDashboardByKey(dashboardKey);
            navigate('/');
        } catch (error) {
            console.error('[Dashboard] delete dashboard error:', error);
        }
    }, [dashboard?.key, dashboard?.title, isPinnedToHome, navigate, persistPinnedDashboardPreference]);

    const handleOpenSettingsModal = useCallback(() => {
        if (!dashboard?.key) return;
        setDashboardSettingsOpen(true);
    }, [dashboard?.key]);

    const handleToggleLayoutEditMode = useCallback(() => {
        setIsLayoutEditMode((prev) => !prev);
    }, []);

    // ----- EFFECTS ----- //

    useEffect(() => {
        setIsLayoutEditMode(false);
    }, [dashboard?.key]);

    // ----- RENDER ----- //

    const pinActionLabel = isPinnedToHome ? 'Unpin from home screen' : 'Pin to home screen';

    return (
        <Section gap="0px">
            <DashboardHeader
              showHeader={showHeader}
              theme={theme}
              dashboard={dashboard}
              dashboardIconKey={dashboardIconKey}
              isPinnedToHome={isPinnedToHome}
              isLayoutEditMode={isLayoutEditMode}
              hideIdentity={hideHeaderIdentity}
              hideActions={actionsInShell}
              handleSelectDashboardIcon={handleSelectDashboardIcon}
              handleDashboardTitleSaved={handleDashboardTitleSaved}
              handleTogglePinToHome={handleTogglePinToHome}
              handleToggleLayoutEditMode={handleToggleLayoutEditMode}
              handleOpenSettingsModal={handleOpenSettingsModal}
              handleDeleteDashboard={handleDeleteDashboard}
              showLayoutEditToggle
            />

            {isLayoutEditMode ? (
                <DashboardLayoutEditor
                  allowedTypeKeys={allowedTypeKeys}
                  childNodesByParent={childNodesByParent}
                  dashboard={dashboard}
                  showLayoutInspector={false}
                  userKey={user?.key || null}
                  views={views}
                  baseNodes={baseNodes}
                />
            ) : (
                <DashboardRenderer
                  childNodesByParent={childNodesByParent}
                  dashboard={dashboard}
                  getType={getType}
                  userKey={user?.key || null}
                  views={views}
                  baseNodes={baseNodes}
                  onOpenView={onOpenView}
                />
            )}

            {actionsInShell ? (
                <ShellSectionActions>
                    <IconPickerButton
                      backgroundColor="transparent"
                      borderRadius={8}
                      buttonSize={34}
                      disabled={!dashboard?.key}
                      hoverBackgroundColor={theme.backgroundHover}
                      iconColor={theme.foreground}
                      iconSize={20}
                      popupHeight={360}
                      popupWidth={380}
                      selectedIconKey={dashboardIconKey}
                      title="Choose dashboard icon"
                      onSelect={handleSelectDashboardIcon}
                    />
                    <IconButton
                      iconKey={isLayoutEditMode ? 'check' : 'edit'}
                      iconSize={20}
                      title={isLayoutEditMode ? 'Done' : 'Edit Layout'}
                      onClick={handleToggleLayoutEditMode}
                    />
                    <IconButton
                      iconKey="keep"
                      iconSize={20}
                      title={pinActionLabel}
                      color={isPinnedToHome ? theme.foreground : theme.foregroundMuted}
                      onClick={handleTogglePinToHome}
                    />
                    <IconButton
                      iconKey="settings"
                      iconSize={20}
                      title="Dashboard settings"
                      onClick={handleOpenSettingsModal}
                    />
                    <IconButton
                      iconKey="delete"
                      iconSize={20}
                      title="Delete dashboard"
                      onClick={handleDeleteDashboard}
                    />
                </ShellSectionActions>
            ) : null}

            <DashboardModal
              isOpen={dashboardSettingsOpen}
              dashboard={dashboard}
              onClose={() => setDashboardSettingsOpen(false)}
            />

            <PeekPanel
              isOpen={isPeekOpen}
              node={peekLocalNode}
              isLoading={isPeekLoading}
              error={peekError}
              onClose={closePeek}
              onCoverSaved={handlePeekCoverSaved}
              onLocationSaved={handlePeekLocationSaved}
              onNodeChange={handlePeekNodeChange}
              onSubtitleSaved={handlePeekSubtitleSaved}
              onTagsSaved={handlePeekTagsSaved}
              onTitleSaved={handlePeekTitleSaved}
            />
        </Section>
    );
});

// Dev Tools
Dashboard.displayName = 'Dashboard';

export default Dashboard;
